import { useTheme } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { AppConfig } from '../../../core';

export default function TermsAndConditions() {
  const { palette, typography } = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: '64px',
      }}
    >
      <Helmet>
        <title>Terms of use | {AppConfig.appName}</title>
        <meta
          name="description"
          content="Terms and conditions for LegacyTies"
        />
      </Helmet>

      <Box
        sx={{
          width: '100%',
          height: 300,
          backgroundColor: palette.primary.main,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            sx={{
              color: palette.primary.contrastText,
              lineHeight: 1.25,
              fontSize: {
                xs: typography.body1.fontSize,
                md: typography.h2.fontSize,
              },
              fontWeight: { xs: 'none', md: typography.h1.fontWeight },
            }}
          >
            Terms and Conditions
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: palette.primary.contrastText }}
          >
            Effective date: 17 August 2026
          </Typography>
        </Container>
      </Box>

      <Box sx={{ width: '100%', py: 4, bgcolor: palette.background.paper }}>
        <Container maxWidth="lg">
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            <strong>1. Introduction</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 3 }}>
            These Terms and Conditions (&quot;Terms&quot;) govern your use of{' '}
            {AppConfig.appName} (&quot;the App&quot;). By accessing or using the
            App, you agree to be bound by these Terms.
          </Typography>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            <strong>2. Your Account</strong>
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0, mb: 3 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              You must provide accurate and current information when creating an
              account.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              You are responsible for maintaining the confidentiality of your
              login details and for all activity under your account.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              You must notify us promptly if you become aware of any
              unauthorised use of your account.
            </ListItem>
          </List>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            <strong>3. Acceptable Use</strong>
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0, mb: 3 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              You must respect the privacy, dignity, and rights of other family
              members and users.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              You may not upload content that is unlawful, harmful, abusive,
              offensive, misleading, or otherwise inappropriate.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              You may not attempt to interfere with the App&apos;s security,
              performance, or availability.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              You must not use automated methods or other means to access or
              extract data without authorisation.
            </ListItem>
          </List>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            <strong>4. User Content</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Users may upload family records, stories, photos, and cultural
            information to the App.
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0, mb: 3 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              You retain ownership of content you upload, subject to any rights
              of third parties.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              By uploading content, you confirm that you have the right to share
              it and that it does not infringe the rights of others.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              We may process, store, display, and otherwise use uploaded content
              as needed to operate and improve the App.
            </ListItem>
          </List>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            <strong>5. Intellectual Property</strong>
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0, mb: 3 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              The App&apos;s design, branding, software, features, and content
              owned by {AppConfig.appName} remain the property of{' '}
              {AppConfig.appName} or its licensors.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              You retain ownership of your own uploaded material, but you grant
              the App a licence to host, process, and display it within the
              service.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              You may not copy, redistribute, or repurpose the App&apos;s
              content or branding without permission.
            </ListItem>
          </List>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            <strong>6. Service Availability and Changes</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 3 }}>
            We may update, improve, suspend, or discontinue features or services
            at any time. We do not guarantee uninterrupted access to the App or
            that all features will always be available.
          </Typography>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            <strong>7. Fees, Payments, and Billing</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            If you use paid features, pricing and billing terms will be shown at
            the point of purchase and may be subject to separate payment
            conditions.
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0, mb: 3 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              You are responsible for any charges associated with your chosen
              plan or additional features.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              We may change pricing or available plans at our discretion, with
              reasonable notice where required.
            </ListItem>
          </List>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            <strong>8. Privacy and Data Protection</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 3 }}>
            Your use of the App is also governed by our Privacy Policy. We will
            process personal data in accordance with applicable data protection
            laws and the information described in that policy.
          </Typography>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            <strong>9. Limitation of Liability</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            To the maximum extent permitted by law, {AppConfig.appName} is not
            liable for indirect, consequential, or incidental damages arising
            from your use of the App.
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3, mt: 0, mb: 3 }}>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              We are not responsible for loss of data caused by user error,
              technical faults, or third-party events outside our control.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              We do not guarantee that information uploaded by users is always
              accurate, complete, or up to date.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
              Our liability is limited to the extent permitted by applicable
              law.
            </ListItem>
          </List>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            <strong>10. Termination</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 3 }}>
            We may suspend or terminate your account if you breach these Terms,
            misuse the App, or engage in conduct that harms the service or other
            users. You may also stop using the App at any time.
          </Typography>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            <strong>11. Governing Law</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 3 }}>
            These Terms are governed by the laws of the United Kingdom.
          </Typography>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            <strong>12. Changes to These Terms</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 3 }}>
            We may update these Terms from time to time. Continued use of the
            App after changes are made indicates your acceptance of the updated
            Terms.
          </Typography>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            <strong>13. Contact</strong>
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            For questions about these Terms, please contact us at{' '}
            {AppConfig.appContactEmail}.
          </Typography>
          <Typography variant="subtitle1">
            We aim to respond within 5 business days.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
